import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── API CONFIGURATION ────────────────────────────────────────────────────────

// Use relative URL to leverage proxy in package.json
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// ─── API FUNCTIONS ─────────────────────────────────────────────────────────────

const knowledgeBaseApi = {
  getAuthToken: () => localStorage.getItem('token'),

  // GET /api/knowledgebase - Fetch all entries
  getAll: async () => {
    try {
      const token = knowledgeBaseApi.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/knowledgebase`, {
        method: 'GET',
        headers: { 
          'Accept': '*/*', 
          'Authorization': `Bearer ${token}` 
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
      
      return response.json();
    } catch (error) {
      console.error('API Error (getAll):', error);
      throw error;
    }
  },

  // POST /api/knowledgebase - Create new entry
  create: async (data) => {
    const token = knowledgeBaseApi.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/knowledgebase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create');
    return response.json();
  },

  // GET /api/knowledgebase/{id} - Get single entry
  getById: async (id) => {
    const token = knowledgeBaseApi.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/knowledgebase/${id}`, {
      method: 'GET',
      headers: { 'Accept': '*/*', 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch entry');
    return response.json();
  },

  // PUT /api/knowledgebase/{id} - Update entry
  update: async (id, data) => {
    const token = knowledgeBaseApi.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/knowledgebase/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update');
    return response.json();
  },

  // DELETE /api/knowledgebase/{id} - Delete entry
  delete: async (id) => {
    const token = knowledgeBaseApi.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/knowledgebase/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete');
    return response.json();
  },

  // POST /api/knowledgebase/{id}/attachments - Upload attachment
  uploadAttachment: async (id, file) => {
    const token = knowledgeBaseApi.getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/knowledgebase/${id}/attachments`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!response.ok) throw new Error('Failed to upload');
    return response.json();
  },

  // GET /api/knowledgebase/attachments/{attachmentId} - Get attachment
  getAttachment: async (attachmentId) => {
    const token = knowledgeBaseApi.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/knowledgebase/attachments/${attachmentId}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch attachment');
    return response.blob();
  },

  // DELETE /api/knowledgebase/attachments/{attachmentId} - Delete attachment
  deleteAttachment: async (attachmentId) => {
    const token = knowledgeBaseApi.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/knowledgebase/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete attachment');
    return response.json();
  }
};

// ─── NAV DATA (shared by desktop sidebar + mobile drawer + bottom nav) ──────

const navItems = [
  { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",     path: "/dashboard"     }] },
  { section: "ENGAGE",    items: [
      { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
      { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
      { icon: "ti-users",        label: "Leads",         path: "/leads"         },
  ]},
  { section: "CONFIGURE", items: [
      { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
      { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
      { icon: "ti-settings", label: "Settings",       path: "/settings"  },
  ]},
];

// ─── DESKTOP SIDEBAR (unchanged look, now hidden on mobile) ──────────────────

function Sidebar() {
  const navigate = useNavigate();
  return (
    <aside className="hidden lg:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}>
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>
      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => {
              const active = path === "/knowledge";
              return (
                <div key={label}
                  onClick={() => navigate(path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all ${
                    active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                             : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}>
                  <i className={`ti ${icon} text-base flex-shrink-0`} />
                  <span className="text-xs font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" />Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">
          © 2026{" "}
          <a
            href="https://awktechz.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-orange-500 transition-colors"
          >
            AWK Technologies
          </a>
        </div>
      </div>
    </aside>
  );
}

// ─── MOBILE DRAWER MENU ──────────────────────────────────────────────────────

function MobileDrawer({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose} />
      <div className="fixed top-0 left-0 bottom-0 z-50 flex flex-col lg:hidden"
        style={{ width: 260, background: "#161412", borderRight: "1px solid #2a2825" }}>
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500" style={{ background: "#252320" }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>

        <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
          {navItems.map(({ section, items }) => (
            <div key={section}>
              <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
              {items.map(({ icon, label, path }) => {
                const active = path === "/knowledge";
                return (
                  <div key={label}
                    onClick={() => { navigate(path); onClose(); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer mb-0.5 transition-all
                      ${active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                               : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}>
                    <i className={`ti ${icon} text-base flex-shrink-0`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-5 pb-6 border-t pt-4" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
            <i className="ti ti-shield-check text-orange-700" />
            Secured workspace
          </div>
          <div className="text-[9px] text-stone-700">
            © 2026{" "}
            <a
              href="https://awktechz.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors"
            >
              AWK Technologies
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MOBILE BOTTOM NAV ───────────────────────────────────────────────────────

function MobileBottomNav() {
  const navigate = useNavigate();
  const items = [
    { icon: "ti-layout-dashboard", label: "Home",  path: "/dashboard"     },
    { icon: "ti-robot",            label: "VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots",     label: "Chats", path: "/conversations" },
    { icon: "ti-users",            label: "Leads", path: "/leads"         },
    { icon: "ti-book",             label: "KB",    path: "/knowledge"     },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around border-t lg:hidden"
      style={{ background: "#161412", borderColor: "#2a2825", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {items.map(({ icon, label, path }) => {
        const active = path === "/knowledge";
        return (
          <button key={path} onClick={() => navigate(path)}
            className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all"
            style={{ color: active ? "#e8602a" : "#6b6560" }}>
            <i className={`ti ${icon} text-lg`} />
            <span style={{ fontSize: 9, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── TOPBAR ───────────────────────────────────────────────────────────────────

function Topbar({ onLogout, onMenuOpen }) {
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);

  return (
    <header className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b flex-shrink-0"
      style={{ background: "#161412", borderColor: "#2a2825" }}>
      {/* Hamburger — mobile only */}
      <button onClick={onMenuOpen}
        className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 flex-shrink-0"
        style={{ background: "#242220" }}>
        <i className="ti ti-menu-2 text-base" />
      </button>

      <div className="hidden lg:flex gap-1.5">
        {["ti-moon-stars", "ti-sun"].map((ic) => (
          <button key={ic}
            className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-300 transition-colors"
            style={{ background: "#242220" }}>
            <i className={`ti ${ic} text-xs`} />
          </button>
        ))}
      </div>
      <div className="flex-1 flex items-center gap-2 rounded-full px-3 lg:px-4 py-2 border lg:mx-4"
        style={{ background: "#1e1c1a", borderColor: "#333" }}>
        <i className="ti ti-search text-stone-600 text-xs" />
        <span className="text-[11px] text-stone-600 hidden sm:block">Search knowledge base…</span>
        <span className="text-[11px] text-stone-600 sm:hidden">Search…</span>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        {/* Logout button with dropdown */}
        <div className="relative">
          <button
            onMouseEnter={() => setShowLogoutDropdown(true)}
            onMouseLeave={() => setShowLogoutDropdown(false)}
            onClick={onLogout}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all duration-200 shadow-lg"
            style={{ background: "#e8602a" }}
            title="Logout"
          >
            <i className="ti ti-power text-lg" />
          </button>

          {showLogoutDropdown && (
            <div
              onMouseEnter={() => setShowLogoutDropdown(true)}
              onMouseLeave={() => setShowLogoutDropdown(false)}
              className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
              style={{ background: "#1a1815", borderColor: "#2a2825" }}
            >
              <button
                onClick={onLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-300 hover:bg-orange-500/10 hover:text-orange-400 transition-colors flex items-center gap-2"
              >
                <i className="ti ti-logout text-orange-500" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── CATEGORY BADGE COLORS ────────────────────────────────────────────────────

const categoryColors = {
  FAQ:           { color: "#f59e0b", bg: "#2d201080" },
  "Product Doc": { color: "#5b8dee", bg: "#1e2d4a80" },
  Policy:        { color: "#a855f7", bg: "#1e103080" },
  Guide:         { color: "#34c97c", bg: "#0d2d1e80" },
  "API Ref":     { color: "#e8602a", bg: "#2d181080" },
  Other:         { color: "#6b7280", bg: "#1a1a1a80" },
};

// ─── INITIAL ENTRIES ──────────────────────────────────────────────────────────

const initialEntries = [
  {
    id: 1,
    category: "Other",
    title: "Fitzone",
    content: "",
    keywords: "fitzone, gym, fitness",
    sortOrder: 0,
    active: true,
    files: ["fitzone_info.pdf"],
  },
];

// ─── DELETE CONFIRM MODAL ─────────────────────────────────────────────────────

function DeleteModal({ entry, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onCancel}>
      <div className="rounded-2xl p-6 border flex flex-col gap-4 w-full sm:w-[400px] max-w-[400px]"
        style={{ background: "#1a1815", borderColor: "#2a2825" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "#e8602a18", border: "1px solid #e8602a40" }}>
            <i className="ti ti-trash text-orange-500 text-xl" />
          </div>
          <div>
            <div className="text-base font-bold text-white">Delete Entry</div>
            <div className="text-xs text-white/60 mt-0.5">This action cannot be undone.</div>
          </div>
        </div>
        <p className="text-sm text-white/80 leading-relaxed">
          Are you sure you want to delete <span className="text-white font-semibold">"{entry.title}"</span>?
          Your AI agent will no longer reference this knowledge.
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel}
            className="flex-1 py-3 rounded-xl text-sm font-medium text-white border hover:text-white/80 transition-colors"
            style={{ background: "#252320", borderColor: "#333" }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-80"
            style={{ background: "#c84a1e" }}>
            Delete Entry
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── VIEW DETAIL MODAL ────────────────────────────────────────────────────────

function ViewDetailModal({ entry, onClose, onEdit, onDownloadAttachment }) {
  const cat = categoryColors[entry.category] || categoryColors["Other"];
  const [isLoading, setIsLoading] = useState(false);
  const [fullEntry, setFullEntry] = useState(entry);

  useEffect(() => {
    // Fetch full entry details using GET /api/knowledgebase/{id}
    async function loadFullDetails() {
      try {
        setIsLoading(true);
        const response = await knowledgeBaseApi.getById(entry.id);
        if (response.success && response.data) {
          setFullEntry(response.data);
        } else if (response.id) {
          setFullEntry(response);
        }
      } catch (err) {
        console.error('Error loading full entry:', err);
        // Use existing entry data as fallback
      } finally {
        setIsLoading(false);
      }
    }
    
    loadFullDetails();
  }, [entry.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}>
      <div className="rounded-2xl border flex flex-col w-full sm:w-[600px] max-w-[600px] max-h-[90vh] overflow-hidden"
        style={{ background: "#1a1815", borderColor: "#2a2825" }}
        onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: cat.bg, border: `1px solid ${cat.color}40` }}>
              <i className="ti ti-book-2 text-xl" style={{ color: cat.color }} />
            </div>
            <div>
              <div className="text-lg font-bold text-white">Entry Details</div>
              <div className="text-xs text-white/60 mt-0.5">Complete information</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 transition-all"
            style={{ background: "#252320" }}>
            <i className="ti ti-x text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: "thin" }}>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <i className="ti ti-loader animate-spin text-orange-500 text-3xl mb-3" />
              <div className="text-sm text-white/60">Loading details...</div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              
              {/* Category & Status */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold rounded-full px-3 py-1.5"
                  style={{ color: cat.color, background: cat.bg }}>
                  {fullEntry.category}
                </span>
                <span className={`flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 ${
                  fullEntry.active ? "text-emerald-400 bg-emerald-950" : "text-stone-400 bg-stone-900"}`}>
                  <i className={`ti ${fullEntry.active ? "ti-circle-check" : "ti-circle-x"} text-xs`} />
                  {fullEntry.active ? "Active" : "Inactive"}
                </span>
              </div>

              {/* Title */}
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wider font-medium mb-2">Title</div>
                <div className="text-xl font-bold text-white">{fullEntry.title}</div>
              </div>

              {/* Content */}
              {fullEntry.content && (
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider font-medium mb-2">Content</div>
                  <div className="rounded-xl p-4 border text-sm text-white/80 leading-relaxed whitespace-pre-wrap"
                    style={{ background: "#252320", borderColor: "#2a2825" }}>
                    {fullEntry.content}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {fullEntry.keywords && (
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider font-medium mb-2">Keywords</div>
                  <div className="flex flex-wrap gap-2">
                    {fullEntry.keywords.split(',').map((keyword, idx) => (
                      <span key={idx} className="rounded-lg px-3 py-1.5 text-sm text-white/80 border"
                        style={{ background: "#252320", borderColor: "#2a2825" }}>
                        <i className="ti ti-tag text-orange-500 text-xs mr-1" />
                        {keyword.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              {fullEntry.attachments && fullEntry.attachments.length > 0 && (
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider font-medium mb-2">
                    Attachments ({fullEntry.attachments.length})
                  </div>
                  <div className="flex flex-col gap-2">
                    {fullEntry.attachments.map((att) => (
                      <div key={att.id} className="flex items-center gap-3 rounded-xl px-4 py-3 border"
                        style={{ background: "#252320", borderColor: "#2a2825" }}>
                        <i className="ti ti-file text-orange-500 text-lg flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-medium truncate">{att.fileName || att.name}</div>
                          {att.fileSize && (
                            <div className="text-xs text-white/60 mt-0.5">{(att.fileSize / 1024).toFixed(1)} KB</div>
                          )}
                        </div>
                        <button
                          onClick={() => onDownloadAttachment(att.id, att.fileName || att.name)}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white border hover:border-orange-600/50 transition-all"
                          style={{ background: "#1a1815", borderColor: "#333" }}>
                          <i className="ti ti-download text-sm" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files (legacy) */}
              {fullEntry.files && fullEntry.files.length > 0 && !fullEntry.attachments && (
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider font-medium mb-2">
                    Files ({fullEntry.files.length})
                  </div>
                  <div className="flex flex-col gap-2">
                    {fullEntry.files.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-3 rounded-xl px-4 py-3 border"
                        style={{ background: "#252320", borderColor: "#2a2825" }}>
                        <i className="ti ti-file text-orange-500 text-lg flex-shrink-0" />
                        <div className="text-sm text-white font-medium flex-1">{file}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sort Order */}
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wider font-medium mb-2">Sort Order</div>
                <div className="text-sm text-white/80">{fullEntry.sortOrder ?? 0}</div>
              </div>

              {/* Metadata */}
              {(fullEntry.createdAt || fullEntry.updatedAt) && (
                <div className="rounded-xl p-4 border" style={{ background: "#252320", borderColor: "#2a2825" }}>
                  <div className="grid grid-cols-2 gap-4">
                    {fullEntry.createdAt && (
                      <div>
                        <div className="text-xs text-white/60 mb-1">Created</div>
                        <div className="text-xs text-white/80">{new Date(fullEntry.createdAt).toLocaleString()}</div>
                      </div>
                    )}
                    {fullEntry.updatedAt && (
                      <div>
                        <div className="text-xs text-white/60 mb-1">Updated</div>
                        <div className="text-xs text-white/80">{new Date(fullEntry.updatedAt).toLocaleString()}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-2 p-6 border-t" style={{ borderColor: "#2a2825" }}>
          <button onClick={() => { onEdit(fullEntry); onClose(); }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white border transition-all hover:border-orange-600/50"
            style={{ background: "#252320", borderColor: "#333" }}>
            <i className="ti ti-pencil text-sm" />
            Edit Entry
          </button>
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white border hover:text-white/80 transition-colors"
            style={{ background: "#252320", borderColor: "#333" }}>
            Close
          </button>
        </div>

      </div>
    </div>
  );
}

// ─── ENTRY CARD ───────────────────────────────────────────────────────────────

function EntryCard({ entry, onEdit, onView, onDelete }) {
  const cat = categoryColors[entry.category] || categoryColors["Other"];
  return (
    <div className="rounded-2xl border flex flex-col gap-3 p-4 lg:p-5 transition-all hover:border-orange-600/30"
      style={{ background: "#1a1815", borderColor: "#2a2825" }}>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold rounded-full px-3 py-1.5"
          style={{ color: cat.color, background: cat.bg }}>
          {entry.category}
        </span>
        <span className={`flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 ${
          entry.active ? "text-emerald-400 bg-emerald-950" : "text-stone-400 bg-stone-900"}`}>
          <i className={`ti ${entry.active ? "ti-circle-check" : "ti-circle-x"} text-xs`} />
          {entry.active ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="text-lg font-bold text-white">{entry.title}</div>

      {entry.content && (
        <p className="text-sm text-white/70 leading-relaxed line-clamp-2">{entry.content}</p>
      )}

      {entry.files && entry.files.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
            style={{ background: "#252320", border: "1px solid #2a2825" }}>
            <i className="ti ti-paperclip text-white/60 text-sm" />
            <span className="text-xs text-white/80 font-medium">
              {entry.files.length} {entry.files.length === 1 ? "file" : "files"}
            </span>
          </div>
          {entry.keywords && (
            <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
              style={{ background: "#252320", border: "1px solid #2a2825" }}>
              <i className="ti ti-tags text-white/60 text-sm" />
              <span className="text-xs text-white/80 font-medium truncate max-w-[120px]">{entry.keywords}</span>
            </div>
          )}
        </div>
      )}

      <div className="border-t" style={{ borderColor: "#2a2825" }} />

      <div className="flex items-center gap-2">
        <button onClick={() => onEdit(entry)}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white border transition-all hover:text-orange-400 hover:border-orange-600/40"
          style={{ background: "#252320", borderColor: "#333" }}>
          <i className="ti ti-pencil text-sm" />Edit
        </button>
        <button onClick={() => onView(entry)}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white border transition-all hover:text-blue-400 hover:border-blue-600/40"
          style={{ background: "#252320", borderColor: "#333" }}>
          <i className="ti ti-eye text-sm" />View
        </button>
        <button onClick={() => onDelete(entry)}
          className="w-10 h-10 rounded-xl flex items-center justify-center border transition-all hover:text-red-400 hover:border-red-900 flex-shrink-0"
          style={{ background: "#252320", borderColor: "#333", color: "#fff" }}>
          <i className="ti ti-trash text-base" />
        </button>
      </div>
    </div>
  );
}

// ─── ENTRY FORM ───────────────────────────────────────────────────────────────

function EntryForm({ entry, onSave, onCancel }) {
  const isEdit = !!entry?.id;
  const [form, setForm] = useState({
    category:  entry?.category  || "FAQ",
    title:     entry?.title     || "",
    content:   entry?.content   || "",
    keywords:  entry?.keywords  || "",
    sortOrder: entry?.sortOrder ?? 0,
    active:    entry?.active    ?? true,
  });
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState(entry?.attachments || []);
  const [dragOver, setDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  function handleFile(fileList) {
    const newFiles = Array.from(fileList);
    setFiles((prev) => [...prev, ...newFiles]);
  }

  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function removeAttachment(attachmentId) {
    try {
      await knowledgeBaseApi.deleteAttachment(attachmentId);
      setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
    } catch (err) {
      setError('Failed to delete attachment: ' + err.message);
    }
  }

  async function handleSubmit() {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      let savedEntry;
      const payload = {
        category: form.category,
        title: form.title,
        content: form.content,
        keywords: form.keywords,
        isActive: form.active,
        sortOrder: form.sortOrder
      };
      
      if (isEdit) {
        savedEntry = await knowledgeBaseApi.update(entry.id, payload);
      } else {
        savedEntry = await knowledgeBaseApi.create(payload);
      }

      // Upload files if any
      if (files.length > 0 && savedEntry.data && savedEntry.data.id) {
        for (const file of files) {
          await knowledgeBaseApi.uploadAttachment(savedEntry.data.id, file);
        }
      }

      onSave();
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  const categories = ["FAQ", "Product Doc", "Policy", "Guide", "API Ref", "Other"];

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6" style={{ background: "#111010" }}>

      <div className="flex flex-col sm:flex-row sm:items-center items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-white leading-tight">
            {isEdit ? "Edit knowledge entry" : "New knowledge entry"}
          </h1>
          <p className="text-sm text-white/70 mt-0.5">
            {isEdit ? "Update a fact your AI can rely on" : "Add a fact your AI can rely on"}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full px-3 py-1.5 border flex-shrink-0"
          style={{ background: "#1a1815", borderColor: "#2a2825" }}>
          <div className="w-6 h-6 rounded-full border-2 border-orange-600"
            style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)" }} />
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-semibold text-stone-200">Sajid Ali</span>
            <span className="text-[9px] text-stone-500">TenantAdmin</span>
          </div>
          <i className="ti ti-chevron-down text-stone-600 text-xs ml-1" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* LEFT: Document upload */}
        <div className="rounded-2xl border flex flex-col gap-4 p-5"
          style={{ background: "#1a1815", borderColor: "#2a2825" }}>
          
          {error && (
            <div className="rounded-xl p-3 border flex items-start gap-2"
              style={{ background: "#2d1010", borderColor: "#5d2020" }}>
              <i className="ti ti-alert-circle text-red-500 text-base flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-red-400">Error</div>
                <div className="text-xs text-red-300 mt-0.5">{error}</div>
              </div>
              <button onClick={() => setError('')} className="text-red-500 hover:text-red-300">
                <i className="ti ti-x text-xs" />
              </button>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-1">
              <i className="ti ti-paperclip text-white/80 text-base" />
              <span className="text-base font-bold text-white">Documents</span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Attach price lists, menus or product images. The AI reads text from .txt and .docx files
              (PDF, image &amp; other types are stored for download). Max 10 MB each.
            </p>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed cursor-pointer transition-all py-10 lg:py-12"
            style={{
              borderColor: dragOver ? "#e8602a" : "#333",
              background: dragOver ? "#e8602a08" : "#252320",
            }}>
            <input ref={fileRef} type="file" multiple className="hidden"
              onChange={(e) => handleFile(e.target.files)} />
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: "#e8602a18", border: "1px solid #e8602a40" }}>
              <i className="ti ti-cloud-upload text-orange-500 text-2xl" />
            </div>
            <div className="text-center px-4">
              <div className="text-base font-bold text-orange-500">Choose files</div>
              <div className="text-sm text-white/60 mt-0.5">You can select more than one</div>
            </div>
          </div>

          {/* Existing attachments */}
          {attachments.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-white">Existing Attachments</div>
              {attachments.map((att) => (
                <div key={att.id} className="flex items-center gap-2 rounded-xl px-3 py-2.5 border"
                  style={{ background: "#252320", borderColor: "#2a2825" }}>
                  <i className="ti ti-file text-orange-500 text-base flex-shrink-0" />
                  <span className="text-sm text-white flex-1 truncate">{att.fileName || att.name}</span>
                  <button onClick={() => removeAttachment(att.id)}
                    className="text-white/60 hover:text-red-400 transition-colors flex-shrink-0">
                    <i className="ti ti-x text-sm" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* New files */}
          {files.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-white">New Files</div>
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl px-3 py-2.5 border"
                  style={{ background: "#252320", borderColor: "#2a2825" }}>
                  <i className="ti ti-file text-orange-500 text-base flex-shrink-0" />
                  <span className="text-sm text-white flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-white/60">({(f.size / 1024).toFixed(1)} KB)</span>
                  <button onClick={() => removeFile(i)}
                    className="text-white/60 hover:text-red-400 transition-colors flex-shrink-0">
                    <i className="ti ti-x text-sm" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Fields */}
        <div className="rounded-2xl border flex flex-col gap-4 p-5"
          style={{ background: "#1a1815", borderColor: "#2a2825" }}>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-white/80 uppercase tracking-wider font-medium">Category</label>
              <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border focus-within:border-orange-600/50 transition-colors"
                style={{ background: "#252320", borderColor: "#333" }}>
                <select className="bg-transparent text-sm text-white outline-none flex-1"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {categories.map((c) => <option key={c} className="bg-stone-900">{c}</option>)}
                </select>
                <i className="ti ti-chevron-down text-white/60 text-sm flex-shrink-0" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-white/80 uppercase tracking-wider font-medium">Title</label>
              <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border focus-within:border-orange-600/50 transition-colors"
                style={{ background: "#252320", borderColor: "#333" }}>
                <input className="bg-transparent text-sm text-white placeholder-white/40 outline-none flex-1"
                  placeholder="e.g. Opening hours"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs text-white/80 uppercase tracking-wider font-medium">Content</label>
              <span className="text-xs text-white/50 italic">optional if you attach a document</span>
            </div>
            <textarea rows={6}
              className="rounded-xl px-3 py-2.5 border text-sm text-white placeholder-white/40 outline-none resize-none focus:border-orange-600/50 transition-colors"
              style={{ background: "#252320", borderColor: "#333" }}
              placeholder="e.g. We are open Mon–Sat 7am to 11pm. Closed on Sundays."
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <p className="text-xs text-white/50 leading-relaxed">
              Leave this empty if you're attaching a document — the AI will read the document's text instead.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs text-white/80 uppercase tracking-wider font-medium">Keywords</label>
              <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border focus-within:border-orange-600/50 transition-colors"
                style={{ background: "#252320", borderColor: "#333" }}>
                <i className="ti ti-tags text-white/60 text-sm flex-shrink-0" />
                <input className="bg-transparent text-sm text-white placeholder-white/40 outline-none flex-1"
                  placeholder="timings, open, schedule (comma separated)"
                  value={form.keywords}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-white/80 uppercase tracking-wider font-medium">Sort Order</label>
              <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border focus-within:border-orange-600/50 transition-colors"
                style={{ background: "#252320", borderColor: "#333" }}>
                <input type="number"
                  className="bg-transparent text-sm text-white outline-none w-full"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
              </div>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3 rounded-xl px-4 py-3 border"
            style={{ background: "#252320", borderColor: "#2a2825" }}>
            <button onClick={() => setForm({ ...form, active: !form.active })}
              className="relative flex-shrink-0 transition-all"
              style={{ width: 40, height: 22 }}>
              <div className="absolute inset-0 rounded-full transition-all"
                style={{ background: form.active ? "#e8602a" : "#444" }} />
              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
                style={{ left: form.active ? "calc(100% - 18px)" : 2 }} />
            </button>
            <div>
              <div className="text-sm font-medium text-white">
                Active — include this in AI answers
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                Toggle off to keep this entry saved but hidden from the AI
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button onClick={handleSubmit}
              disabled={!form.title.trim() || isLoading}
              className="flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed w-full sm:w-auto"
              style={{ background: "#e8602a" }}>
              {isLoading ? (
                <>
                  <i className="ti ti-loader animate-spin text-base" />
                  {isEdit ? "Updating..." : "Saving..."}
                </>
              ) : (
                <>
                  <i className="ti ti-check text-base" />
                  {isEdit ? "Update entry" : "Save entry"}
                </>
              )}
            </button>
            <button onClick={onCancel}
              disabled={isLoading}
              className="rounded-xl px-5 py-3 text-sm font-medium text-white border hover:text-white/80 transition-colors w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "#252320", borderColor: "#333" }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN KNOWLEDGE BASE CONTENT ──────────────────────────────────────────────

function KnowledgeBaseContent() {
  const [entries, setEntries]           = useState([]);
  const [view, setView]                 = useState("list");
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewTarget, setViewTarget]     = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState('');

  // Load entries on mount
  useEffect(() => {
    loadEntries();
  }, []);

  async function loadEntries() {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('🔍 Loading entries from:', API_BASE_URL + '/knowledgebase');
      console.log('🔑 Token:', knowledgeBaseApi.getAuthToken() ? 'Present' : 'Missing');
      
      const response = await knowledgeBaseApi.getAll();
      console.log('✅ Response:', response);
      
      if (response.success && response.data) {
        setEntries(response.data);
      } else if (Array.isArray(response)) {
        // Handle direct array response
        setEntries(response);
      } else {
        console.warn('Unexpected response format:', response);
        setEntries([]);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to load entries';
      setError(errorMsg);
      console.error('❌ Error loading entries:', err);
      
      // Show more helpful error messages
      if (errorMsg.includes('Failed to fetch')) {
        setError('Cannot connect to API server. Please check:\n1. Backend API is running\n2. You are logged in\n3. Network connection is stable');
      }
    } finally {
      setIsLoading(false);
    }
  }

  const categories = ["All", "FAQ", "Product Doc", "Policy", "Guide", "API Ref", "Other"];

  const filtered = entries.filter((e) =>
    activeFilter === "All" ? true : e.category === activeFilter
  );

  function handleSave() {
    // Refresh the list after save
    loadEntries();
    setView("list");
    setEditingEntry(null);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      setError('');
      await knowledgeBaseApi.delete(deleteTarget.id);
      await loadEntries(); // Reload list
      setDeleteTarget(null);
    } catch (err) {
      setError('Failed to delete entry: ' + err.message);
      console.error('Error deleting entry:', err);
    }
  }

  async function handleDownloadAttachment(attachmentId, fileName) {
    try {
      console.log('📥 Downloading attachment:', attachmentId);
      const blob = await knowledgeBaseApi.getAttachment(attachmentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download initiated');
    } catch (err) {
      setError('Failed to download attachment: ' + err.message);
      console.error('Error downloading attachment:', err);
    }
  }

  if (view === "form") {
    return (
      <EntryForm
        entry={editingEntry}
        onSave={handleSave}
        onCancel={() => { setView("list"); setEditingEntry(null); }}
      />
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6" style={{ background: "#111010" }}>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-extrabold text-white leading-tight">Knowledge Base</h1>
          <p className="text-sm text-white/70 mt-0.5">The facts your AI uses to answer customers</p>
        </div>
        <button onClick={() => { setEditingEntry(null); setView("form"); }}
          className="flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity w-full sm:w-auto flex-shrink-0"
          style={{ background: "#e8602a" }}>
          <i className="ti ti-plus text-base" />Add entry
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 rounded-xl p-4 border flex items-start gap-3"
          style={{ background: "#2d1010", borderColor: "#5d2020" }}>
          <i className="ti ti-alert-circle text-red-500 text-xl flex-shrink-0" />
          <div className="flex-1">
            <div className="text-base font-semibold text-red-400">Error</div>
            <div className="text-sm text-red-300 mt-1">{error}</div>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-300">
            <i className="ti ti-x text-sm" />
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: "#e8602a18", border: "1px solid #e8602a40" }}>
            <i className="ti ti-loader animate-spin text-orange-500 text-2xl" />
          </div>
          <div className="text-base font-semibold text-white">Loading knowledge base...</div>
          <div className="text-sm text-white/60 mt-1">Please wait</div>
        </div>
      ) : (
        <>
      {/* Entry count bar */}
      <div className="rounded-2xl border px-4 lg:px-5 py-4 mb-5 flex flex-col lg:flex-row lg:items-center justify-between gap-3"
        style={{ background: "#1a1815", borderColor: "#2a2825" }}>
        <div>
          <div className="text-2xl font-bold text-white">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </div>
          <div className="text-sm text-white/70 mt-0.5">
            Hours, pricing, products and policies — grounding for every reply.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: "Active",   value: entries.filter((e) => e.active).length,  color: "#34c97c", bg: "#0d2d1e" },
            { label: "Inactive", value: entries.filter((e) => !e.active).length, color: "#6b7280", bg: "#1a1a1a" },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 border"
              style={{ background: bg, borderColor: `${color}30` }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
              <span className="text-[10px] font-medium" style={{ color }}>{value} {label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Category filter pills */}
      <div className="flex items-center gap-2 mb-5 overflow-x-auto lg:flex-wrap lg:overflow-visible pb-1 lg:pb-0"
        style={{ scrollbarWidth: "none" }}>
        {categories.map((cat) => {
          const cc = categoryColors[cat];
          return (
            <button key={cat} onClick={() => setActiveFilter(cat)}
              className="text-sm font-medium rounded-full px-4 py-2 border transition-all flex-shrink-0"
              style={activeFilter === cat
                ? { background: cc ? cc.bg : "#e8602a22", color: cc ? cc.color : "#e8602a", borderColor: cc ? `${cc.color}50` : "#e8602a50" }
                : { background: "#1a1815", color: "#fff", borderColor: "transparent" }}>
              {cat}
              <span className="ml-1.5 text-xs opacity-60">
                ({cat === "All" ? entries.length : entries.filter((e) => e.category === cat).length})
              </span>
            </button>
          );
        })}
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 lg:py-24 gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: "#1a1815", border: "1px solid #2a2825" }}>
            <i className="ti ti-book-off text-3xl text-white/40" />
          </div>
          <div className="text-center px-4">
            <div className="text-base font-bold text-white">No entries yet</div>
            <div className="text-sm text-white/60 mt-1">
              Add your first knowledge entry so your AI can start answering with facts.
            </div>
          </div>
          <button onClick={() => { setEditingEntry(null); setView("form"); }}
            className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
            style={{ background: "#e8602a" }}>
            <i className="ti ti-plus text-base" />Add your first entry
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((entry) => (
            <EntryCard key={entry.id} entry={entry}
              onEdit={(e) => { setEditingEntry(e); setView("form"); }}
              onView={setViewTarget}
              onDelete={setDeleteTarget} />
          ))}

          {/* Ghost add card */}
          <button onClick={() => { setEditingEntry(null); setView("form"); }}
            className="rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 py-10 cursor-pointer transition-all hover:border-orange-600/50 hover:bg-white/[0.01]"
            style={{ borderColor: "#2a2825" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "#e8602a18", border: "1px solid #e8602a40" }}>
              <i className="ti ti-plus text-orange-500 text-lg" />
            </div>
            <div className="text-sm font-medium text-white">Add new entry</div>
          </button>
        </div>
      )}

      {deleteTarget && (
        <DeleteModal
          entry={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {viewTarget && (
        <ViewDetailModal
          entry={viewTarget}
          onClose={() => setViewTarget(null)}
          onEdit={(e) => { setEditingEntry(e); setView("form"); setViewTarget(null); }}
          onDownloadAttachment={handleDownloadAttachment}
        />
      )}
        </>
      )}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────

export default function KnowledgeBasePage({ onLogout }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden" style={{ background: "#111010" }}>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar onLogout={onLogout} onMenuOpen={() => setDrawerOpen(true)} />
        <KnowledgeBaseContent />
      </div>

      {/* Mobile bottom nav */}
      <MobileBottomNav />
    </div>
  );
}